import { type PointerEvent } from 'react';

import { useTooltip } from '../../../core/components/overlay-tooltip/contexts/TooltipContext.js';
import type { Data } from '../../../core/components/overlay-tooltip/types.js';
import { useTooltipActions } from '../../../core/components/overlay-tooltip/useTooltipActions.js';
import { TOOLTIP_VARIANT } from '../constants.js';
import { useMultiMeterBarChartTooltipData } from '../context/MultiMeterBarChartTooltipContext.js';

export const useMultiMeterBarChartTooltipHandlers = (
  data: Data,
  setHoveredSegmentIndex: (index: number | undefined) => void,
) => {
  const tooltip = useTooltip();
  const { hover, pin, unpin, outOfBounds } = useTooltipActions();
  const { tooltipConfig } = useMultiMeterBarChartTooltipData();

  const isSharedTooltip =
    tooltipConfig.variant === TOOLTIP_VARIANT.Shared ||
    !!tooltipConfig.template;

  const buildSingleSeriesData = (segmentEl: Element) => {
    const index = Number(segmentEl.getAttribute('data-segment-index'));
    const series = [data.series[index]];
    return {
      geometry: 'multimeterbar' as const,
      series,
    };
  };

  const onClick = (event: PointerEvent<HTMLDivElement>) => {
    if (tooltip.getState().pinned) {
      unpin();
    } else {
      if (isSharedTooltip) {
        pin(event, data);
      } else {
        const segmentEl = (event.target as Element).closest(
          '[data-segment-index]',
        );
        if (segmentEl) {
          pin(event, buildSingleSeriesData(segmentEl));
        }
      }
    }
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const segmentEl = (event.target as Element).closest('[data-segment-index]');
    if (isSharedTooltip) {
      if (!tooltip.getState().pinned) {
        setHoveredSegmentIndex(
          segmentEl
            ? Number(segmentEl.getAttribute('data-segment-index'))
            : undefined,
        );
      }
      hover(event, data);
    } else if (segmentEl) {
      hover(event, buildSingleSeriesData(segmentEl));
    } else {
      outOfBounds();
    }
  };

  const onPointerLeave = () => {
    outOfBounds();
  };

  return { onClick, onPointerMove, onPointerLeave };
};
